// A person's moves on a pool, and the platform-wide stop. Each one asks why
// in a dialog (no window.prompt) and goes through the API route that calls
// the function the terminal uses; the answer, or the refusal, is shown in the
// dialog, and nothing is typed twice. Admin only — the API refuses anybody
// else, so nobody else is shown a button that would only be refused.

import { useId, useState } from "react";
import { toast } from "sonner";
import { PauseIcon, PlayIcon, RotateCcwIcon } from "lucide-react";
import {
  pauseWarmupPool,
  releaseWarmupPool,
  startWarmup,
} from "@/lib/api";
import type { WarmupKnobs, WarmupPool } from "@/lib/api";
import { useAuth } from "@/lib/auth";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { ActionDialog } from "@/components/emc/ActionDialog";
import { Callout } from "@/components/emc/states";
import { count } from "@/components/emc/format";
import { perIpOf, releaseCopy, stopReason } from "@/pages/warmup/model";

// The API's note limit (`WarmupNoteIn`, `SendingHaltIn`).
const NOTE_MAX = 300;

type Move = "start" | "pause" | "release";

interface Props {
  pool: WarmupPool;
  knobs: WarmupKnobs;
  /** Called after a move went through, so the page reads the pool again. */
  onChanged: () => void;
  size?: "sm" | "default";
}

export function useIsAdmin(): boolean {
  const { user } = useAuth();
  return user?.role === "admin";
}

export function PoolActions({ pool, knobs, onChanged, size = "sm" }: Props) {
  const isAdmin = useIsAdmin();
  const [open, setOpen] = useState<Move | null>(null);
  const [perIp, setPerIp] = useState("");
  const perIpId = useId();
  if (!isAdmin) return null;

  const code = pool.code;
  const release = releaseCopy(pool);
  const start = Math.min(knobs.start_per_ip, pool.target_daily_per_ip);
  const growth = knobs.growth.toLocaleString("hu-HU", { maximumFractionDigits: 2 });

  function done(message: string) {
    toast(message);
    onChanged();
  }

  return (
    <div className="flex flex-wrap items-center gap-2">
      {pool.can_start && (
        <Button
          size={size}
          type="button"
          onClick={() => {
            setPerIp("");
            setOpen("start");
          }}
        >
          <PlayIcon />
          Warmup indítása
        </Button>
      )}
      {pool.can_release && (
        <Button size={size} type="button" onClick={() => setOpen("release")}>
          <RotateCcwIcon />
          Feloldás
        </Button>
      )}
      {pool.can_pause && (
        <Button size={size} variant="outline-danger" type="button" onClick={() => setOpen("pause")}>
          <PauseIcon />
          Megállítás
        </Button>
      )}

      <ActionDialog
        open={open === "start"}
        onOpenChange={(next) => setOpen(next ? "start" : null)}
        title={`A(z) ${code} pool warmupjának indítása`}
        description={
          <>
            A pool hideg: ma semmit sem küld. Indítás után a limit {count(start)} / IP / nap (
            {pool.ip_count} IP: {count(start * pool.ip_count)} levél ma), és az ütemező minden
            éjfélkor dönt: tiszta nap után ×{growth}, a 4. szabály küszöbei fölött megáll.
          </>
        }
        confirmLabel="Warmup indítása"
        reason={{
          label: "Miért indul most?",
          placeholder: "pl. első hullám: a 30 napon belül kattintók",
          max: NOTE_MAX,
        }}
        onConfirm={async (note) => {
          const limit = perIpOf(perIp, start);
          await startWarmup(code, note, limit);
          done(`A(z) ${code} pool warmupja elindult.`);
        }}
      >
        <div className="flex flex-col gap-1.5">
          <Label htmlFor={perIpId}>Induló limit / IP / nap (nem kötelező)</Label>
          <Input
            id={perIpId}
            inputMode="numeric"
            value={perIp}
            placeholder={String(start)}
            onChange={(event) => setPerIp(event.target.value)}
          />
          <p className="text-label text-muted-foreground">
            Üresen a beállított {count(start)}; kevesebb kérhető, több nem — a rámpa innen emel.
          </p>
        </div>
      </ActionDialog>

      <ActionDialog
        open={open === "pause"}
        onOpenChange={(next) => setOpen(next ? "pause" : null)}
        title={`A(z) ${code} pool megállítása`}
        description={
          <>
            A pool azonnal áll: a limiter a következő küldési kérésnél megtagadja. A warmup és
            az őrszem is megállított állapotba kerül, és kritikus riasztás megy ki. A limit marad (
            {count(pool.daily_limit_per_ip)} / IP): a feloldás onnan folytatja.
          </>
        }
        confirmLabel="Pool megállítása"
        tone="danger"
        reason={{ label: "Miért állítod meg?", placeholder: "pl. furcsa a Gmail-kézbesítés", max: NOTE_MAX }}
        onConfirm={async (note) => {
          await pauseWarmupPool(code, note);
          done(`A(z) ${code} pool megállt.`);
        }}
      />

      <ActionDialog
        open={open === "release"}
        onOpenChange={(next) => setOpen(next ? "release" : null)}
        title={`A(z) ${code} pool feloldása`}
        description={release.description}
        confirmLabel="Pool feloldása"
        reason={{ label: "Miért engeded tovább?", placeholder: "pl. megnéztem, a jel elmúlt", max: NOTE_MAX }}
        onConfirm={async (note) => {
          await releaseWarmupPool(code, note);
          done(`A(z) ${code} pool újra megy.`);
        }}
      >
        {stopReason(pool) && (
          <p className="text-body">
            <span className="text-muted-foreground">Ami megállította: </span>
            {stopReason(pool)}
          </p>
        )}
        {release.warning && <Callout tone="warn">{release.warning}</Callout>}
      </ActionDialog>
    </div>
  );
}

// The platform-wide stop (F1) lives with the shell's halt bar; the warmup
// page shows it in its header, the sidebar in its footer.
export { HaltButton } from "@/components/HaltButton";
